import { RESOURCES } from '@constants';
import { useCurrentUser } from '@hooks/use-current-user';
import { useTranslations } from '@i18n/use-translations';
import { useInvalidate, UseInvalidateProp } from '@refinedev/core';
import { App } from 'antd/es';
import type { HubConnection } from '@microsoft/signalr';
import React, { createContext, useContext, useEffect, useState } from 'react';

const SignalRContext = createContext<HubConnection | null>(null);

const eventName = 'AckMessage';

enum EventTypes {
  ProfileEnabled = 1,
}

enum ResponseStatus {
  Pending = 1,
  Ok,
  Error,
  Timeout,
}

type SignalRPayloadType = {
  command: EventTypes.ProfileEnabled;
  status: ResponseStatus;
  payload: {
    iccid: string;
    deviceId: string;
    deviceName: string;
  };
};

export const SignalRContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useCurrentUser();
  const isAuthenticated = !!user;

  const t = useTranslations('signalR');

  const invalidate = useInvalidate();
  const { notification } = App.useApp();
  const [signalRLib, setSignalRLib] = useState<typeof import('@microsoft/signalr') | null>(null);
  const [connection, setConnection] = useState<HubConnection | null>(null);

  //import the lib and save it to state
  useEffect(() => {
    if (signalRLib) return;
    import('@microsoft/signalr').then(signalR => setSignalRLib(signalR));
  }, [signalRLib]);

  //connect to signalR
  useEffect(() => {
    if (typeof window === 'undefined' || connection || !signalRLib || !isAuthenticated) return;
    const init = async () => {
      const signalRUrl = `/client-proxy/${RESOURCES.signalR}`;

      const connect = new signalRLib.HubConnectionBuilder()
        .withUrl(signalRUrl, { withCredentials: true })
        .withAutomaticReconnect()
        .build();

      await connect
        .start()
        .then(() => setConnection(connect))
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error('SignalR connection error:', err);
        });

      return () => {
        connect.stop();
        setConnection(null);
      };
    };

    init();
  }, [connection, signalRLib, notification, isAuthenticated]);

  //SUBSCRIBE TO GLOBAL EVENTS
  useEffect(() => {
    if (!connection) return;

    const signalRHandler = ({ command, status, payload }: SignalRPayloadType) => {
      switch (command) {
        case EventTypes.ProfileEnabled: {
          const isSuccess = status === ResponseStatus.Ok;
          const { deviceId, deviceName, iccid } = payload;
          if (isSuccess) {
            notification.success({
              message: t('events.profileEnabled.successTitle'),
              description: t('events.profileEnabled.successMessage', {
                iccid,
                deviceName,
                deviceId,
              }),
            });
            const invalidateQueries: UseInvalidateProp[] = [
              { resource: RESOURCES.profiles, invalidates: ['list'] },
              { resource: RESOURCES.devices, invalidates: ['list'] },
              { resource: RESOURCES.devices, invalidates: ['detail'], id: deviceId },
            ];
            invalidateQueries.forEach(invalidateQuery => invalidate(invalidateQuery));
          } else {
            notification.error({
              message: t('events.profileEnabled.failureTitle'),
              description: t('events.profileEnabled.failureMessage', {
                iccid,
                deviceName,
                deviceId,
              }),
            });
          }
        }
      }
    };

    connection.on(eventName, signalRHandler);

    return () => {
      connection.off(eventName, signalRHandler);
    };
  }, [connection, invalidate, notification, t]);

  //UNSUBSCRIBE ON LOGOUT
  useEffect(() => {
    if (connection && !isAuthenticated) {
      setConnection(null);
      connection.stop();
    }
  }, [isAuthenticated, connection]);

  return <SignalRContext.Provider value={connection}>{children}</SignalRContext.Provider>;
};

export const useSignalR = () => useContext(SignalRContext);
